import { z } from "zod";
import { Timestamps } from "../../common/index.ts";
import { DocumentTypeDto } from "../document-types/document-types.schema.ts";
import { EntityDto } from "../entities/entities.schema.ts";
import {
  DocumentId,
  DocumentTypeId,
  EntityId,
  StateId,
  CustomerId,
  TransitionId,
  TransitionLogId,
} from "../fsm.schema.ts";
import { StateHydrated } from "../states/states.schema.ts";
import {
  TransitionDto,
  TransitionErrorCode,
} from "../transitions/transitions.schema.ts";

/**
 * @description A Document represents a specific instance of a DocumentType that is being tracked in the FSM.
 */
export const DocumentDto = z
  .object({
    id: DocumentId,
    customerId: CustomerId,
    name: z.string().min(1),
    documentTypeId: DocumentTypeId,
    currentStateId: StateId,
    assignedToEntityId: EntityId.nullable(),
  })
  .merge(Timestamps);

export type DocumentDto = z.infer<typeof DocumentDto>;

/**
 * @description A TransitionLog represents a record of a transition execution for a specific Document.
 */
export const TransitionLogDto = z
  .object({
    id: TransitionLogId,
    documentId: DocumentId,
    transitionId: TransitionId,
    fromStateId: StateId,
    toStateId: StateId,
    executedByEntityId: EntityId.nullable(),
    executedAt: z.string().datetime(),
    fieldValues: z.record(z.string(), z.unknown()).default({}),
  })
  .merge(Timestamps);

export type TransitionLogDto = z.infer<typeof TransitionLogDto>;

export const DocumentHydrated = DocumentDto.extend({
  documentType: DocumentTypeDto,
  currentState: StateHydrated,
  assignedTo: EntityDto,
  availableTransitions: z.array(TransitionDto),
});

export type DocumentHydrated = z.infer<typeof DocumentHydrated>;

export const CreateDocumentInput = z.object({
  name: z.string().min(1),
  documentTypeId: DocumentTypeId,
  assignedToEntityId: EntityId,
});
export type CreateDocumentInput = z.infer<typeof CreateDocumentInput>;

export const ExecuteTransitionCommand = z.object({
  documentId: DocumentId,
  transitionId: TransitionId,
  executedByEntityId: EntityId,
  fieldValues: z.record(z.string(), z.unknown()).optional(),
});

export type ExecuteTransitionCommand = z.infer<typeof ExecuteTransitionCommand>;

export const ExecuteTransitionResult = z.discriminatedUnion("success", [
  z.object({
    success: z.literal(true),
    document: DocumentDto,
    transitionLog: TransitionLogDto,
  }),
  z.object({
    success: z.literal(false),
    error: TransitionErrorCode,
  }),
]);

export type ExecuteTransitionResult = z.infer<typeof ExecuteTransitionResult>;
